import React, { useCallback, useEffect, useRef, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { dataLayerPush } from '@r1-frontend/analytics/dataLayerPush';

import { BlindMenu } from '@r1-frontend/ui-react/components/BlindMenu';
import { BlindMenuWithBtn } from '@r1-frontend/ui-react/components/BlindMenu/BlindMenuWithBtn';
import Button from '@r1-frontend/ui-react/components/buttons/button';
import { DocumentText } from '@r1-frontend/ui-react/components/svg/content';
import { User } from '@r1-frontend/ui-react/components/svg/users';
import { CATEGORIES, EVENTS } from '@r1-frontend/shared/constants/analytics';
import { useMatchMediaByWidth } from '@r1-frontend/shared/hooks/useMatchMedia';

import { Agreement } from '~/src/features/_layout/Header/commonComponents/AgreementInfo';
import { breakpoints } from '~/src/features/_layout/Header/types';
import { useClickOutside } from '~/src/helpers/hooks/useClickOutside';
import { logout } from '~/src/store/actions/auth/logout';
import { fetchClientInfo } from '~/src/store/actions/clientInfo';
import { selectClientPersonalLinksForHeader } from '~/src/store/selectors/auth/selectClientProducts';

import * as ST from './styled';

const ProfileContainer = () => {
    const { links, linksForActionSearch, isNeedToRequest } = useSelector(selectClientPersonalLinksForHeader);
    const dispatch = useDispatch();

    useEffect(()=> {
        if (isNeedToRequest) {
            // todo-porotikova свести к 'personal', 'contacts'
            dispatch(fetchClientInfo());
        }
    }, [dispatch, isNeedToRequest]);

    const sendEvent = useCallback((action) => {
        if (action) {
            dataLayerPush({
                event: EVENTS.UAevent,
                category: CATEGORIES.profileMenu,
                action,
            });
        }
    }, []);

    const onClickLinkHandler = useCallback((id: string) => {
        const item = linksForActionSearch[id];

        sendEvent(item.action);
        if (item.isLogout) {
            dispatch(logout());
        }
    }, [dispatch, linksForActionSearch, sendEvent]);

    const blindMenuRef = useRef<HTMLDivElement>(null);
    const agreementMenuRef = useRef<HTMLDivElement>(null);

    const [isDisplay, setIsDisplay] = useState(false);
    const [isDisplayAgreement, setIsDisplayAgreement] = useState(false);

    const isMobile = useMatchMediaByWidth(breakpoints.mobile, false, true);

    const onOutClick = useCallback((event) => {
        const path = event.composedPath();
        const isBlindMenu = path.includes(blindMenuRef?.current);
        const isAgreementMenu = path.includes(agreementMenuRef?.current);

        if (!isBlindMenu) {
            setIsDisplay(false);
        }

        if (!isAgreementMenu) {
            setIsDisplayAgreement(false);
        }
    }, []);

    useClickOutside(!isDisplay, onOutClick);
    useClickOutside(!isDisplayAgreement, onOutClick);

    const agreementDetailClickHandler = useCallback(() => {
        setIsDisplayAgreement(prevState => !prevState);
        if (!isDisplayAgreement) {
            dataLayerPush({
                event: EVENTS.UAevent,
                category: CATEGORIES.agreementMenu,
                action: 'open_agreement_details_menu',
            });
        }
    }, [isDisplayAgreement]);

    const profileMenuClickHandler = useCallback(() => {
        setIsDisplay(prevState => !prevState);
        if (!isDisplay) {
            dataLayerPush({
                event: EVENTS.UAevent,
                category: CATEGORIES.profileMenu,
                action: 'open_profile_menu',
            });
        }
    }, [isDisplay]);

    return (
        <ST.ProfileAuthWrapper>
            <BlindMenu
                activeComponent={<Button
                    rounded={true}
                    bType={'secondary'}
                    size={'medium'}
                    Icon={<DocumentText size={20}/>}
                />}
                blindMenuRef={agreementMenuRef}
                isMobileView={isMobile}
                displayDropDown={isDisplayAgreement}
                onClick={agreementDetailClickHandler}
            >
                <Agreement/>
            </BlindMenu>
            <BlindMenuWithBtn
                Button={<Button
                    rounded={true}
                    bType={'secondary'}
                    size={'medium'}
                    Icon={<User size={20}/>}
                />}
                blindMenuRef={blindMenuRef}
                isMobileView={isMobile}
                elements={links}
                displayDropDown={isDisplay}
                onClick={profileMenuClickHandler}
                onElementClick={onClickLinkHandler}
                data-test='auth-menu'
            />
        </ST.ProfileAuthWrapper>
    );
};

export default ProfileContainer;
